import { DatePickerRange, SelectOption } from '../components';
import { ACTIVE_TAB_PARAM, AppRoutes, DEFAULT_ANALYTICS_AVERAGE_BY, DEFAULT_ANALYTICS_DATE_RANGE } from '../constants';
import {
  AnalyticsFilterKeys,
  AnalyticsFilters,
  AnalyticsTab,
  AppUser,
  Filters,
  RealCallsFilterKeys,
  ReviewFilters,
  ReviewTab,
  Team,
} from '../types';
import { getAnalyticsActiveTab } from './analytics.utils';
import { getCurrentAppRoute } from './app.utils';
import { formatDisplayedPhoneNumber, snakeCaseToLabel } from './string.utils';

/**
 * Parses a required value into a SelectOption shape (label and value).
 */
export const parseRequiredSelectOption = (value: string): SelectOption => ({ label: snakeCaseToLabel(value), value });

/**
 * Creates user options from user data.
 */
export const getUserOptions = (userData?: { users: AppUser[]; phoneNumbers?: string[] }) => {
  const mappedUsers = userData?.users.map(({ id, name = '' }) => ({ value: id, label: name })) || [];
  const mappedPhoneNumbers = (userData?.phoneNumbers || []).map((phoneNumber) => ({
    value: phoneNumber,
    label: formatDisplayedPhoneNumber(phoneNumber),
  }));
  return mappedUsers.concat(mappedPhoneNumbers);
};

/**
 * Gets the date range parameter from the start and end dates.
 */
export const getDateRangeParam = (range: DatePickerRange): string => `${range.start.getTime()}-${range.end.getTime()}`;

/**
 * Gets the team options with the user's teams first.
 */
export const getTeamsOptions = (orgTeams: Team[], userTeams: Team[]): SelectOption[] => {
  return orgTeams
    .map(({ id, name, color }) => {
      const isUserTeam = userTeams.some((userTeam) => userTeam.id === id);
      return {
        value: id,
        label: `${name}${isUserTeam ? ' (your team)' : ''}`,
        color,
        isUserTeam,
      };
    })
    .sort(({ isUserTeam: a }, { isUserTeam: b }) => (b ? 1 : 0) - (a ? 1 : 0));
};

/**
 * Determines whether the analytics filters have been modified from their default settings
 * by comparing the current filter values to the predefined default values.
 */
const isAnalyticsFiltersDirty = (filters: AnalyticsFilters) => {
  const dateRangeFilter = filters[AnalyticsFilterKeys.DATE_RANGE];

  const isDefaultDateRangeFilter =
    dateRangeFilter &&
    dateRangeFilter.start.getTime() === DEFAULT_ANALYTICS_DATE_RANGE.start.getTime() &&
    dateRangeFilter.end.getTime() === DEFAULT_ANALYTICS_DATE_RANGE.end.getTime();

  // For all tabs, if the date range filter is not the default, the filters are dirty.
  if (!isDefaultDateRangeFilter) return true;

  const activeTab = getAnalyticsActiveTab();
  const isProgressReportTab = activeTab === AnalyticsTab.PROGRESS_REPORT;

  // For all tabs other than the progress report tab, the filters are dirty if there are any filters other than the date range.
  if (!isProgressReportTab) return Object.keys(filters).length > 1;

  const averageByFilter = filters[AnalyticsFilterKeys.AVERAGE_BY]?.[0];
  const isDefaultAverageByFilter = averageByFilter === DEFAULT_ANALYTICS_AVERAGE_BY;

  // For the progress report tab, the filters are dirty if the average by filter is not the default or if there are more than two filters.
  return !isDefaultAverageByFilter || Object.keys(filters).length > 2;
};

/**
 * Determines whether the review filters have been modified considering the active tab.
 */
const isReviewFiltersDirty = (filters: ReviewFilters) => {
  const params = new URLSearchParams(window.location.search);
  const activeTab = params.get(ACTIVE_TAB_PARAM) as ReviewTab;

  let filteredFilters = { ...filters };

  // If on real calls tab, only consider filters that are valid for real calls
  if (activeTab === ReviewTab.REAL_CALLS) {
    filteredFilters = Object.fromEntries(
      Object.entries(filters).filter(([key]) => Object.values(RealCallsFilterKeys).includes(key as RealCallsFilterKeys))
    );
  }

  return Object.keys(filteredFilters).length > 0;
};

/**
 * Determines whether the filters have been modified from their default settings.
 */
export const isFiltersDirty = (filters: Filters) => {
  const currRoute = getCurrentAppRoute();
  if (currRoute === AppRoutes.ANALYTICS) {
    return isAnalyticsFiltersDirty(filters as AnalyticsFilters);
  } else if (currRoute === AppRoutes.REVIEW) {
    return isReviewFiltersDirty(filters as ReviewFilters);
  } else {
    return Object.keys(filters).length > 0;
  }
};
